import { BUILTIN_MODELS, DEVICE_ROLES, DeviceModel as CatalogModel, DeviceRole, DeviceSetup } from '@iws/pos-devices'
import { toast } from 'sonner'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/contexts/AuthContext'
import { Terminal, TerminalModel } from '@/http/models/Terminal'

const ROLE_LABEL: Record<DeviceRole, string> = {
    receipt: 'Receipts and slips',
    label: 'Labels',
    document: 'A4 documents and label sheets',
    scale_read: 'Weighing at the POS',
    scale_labels: 'Scale labels (PLU list)',
    cash_drawer: 'Cash drawer',
    customer_display: 'Customer display',
}

const TRANSPORT_LABEL: Record<string, string> = { browser: 'Browser', webusb: 'USB', webserial: 'Serial', imin: 'iMin', http: 'Network' }

const errorOf = (error: unknown, fallback: string) => (error as { data?: { message?: string } } | null)?.data?.message ?? fallback

const EMPTY: DeviceSetup = { devices: [], roles: {}, options: {} }

/**
 * Each till's devices (POS.md §18.9), as the till last backed them up. Pairing a USB or serial device happens on the
 * till itself (the browser grants access there); here the owner renames, removes, and re-maps roles and options. The
 * till picks the change up on its next settings refresh.
 */
export const TillDevices = () => {
    const { can } = useAuth()
    const [terminals, setTerminals] = useState<Terminal[]>([])
    const [catalog, setCatalog] = useState<CatalogModel[]>([])
    const [loading, setLoading] = useState(true)
    const [editing, setEditing] = useState<Terminal | null>(null)
    const [draft, setDraft] = useState<DeviceSetup>(EMPTY)
    const [saving, setSaving] = useState(false)

    const models = useMemo(() => [...BUILTIN_MODELS, ...catalog], [catalog])
    const modelOf = (id: string) => models.find((m) => m._id === id)
    const editable = can('devices:setup')

    const load = useCallback(async () => {
        setLoading(true)
        const [list, cat] = await Promise.all([TerminalModel.getList(), TerminalModel.getDeviceModels()])
        if (list.success && list.data) setTerminals(list.data.result)
        else toast.error(errorOf(list.error, 'Could not load terminals'))
        if (cat.success && cat.data) setCatalog(cat.data.result)
        setLoading(false)
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const open = (t: Terminal) => {
        setDraft(t.device_setup ?? EMPTY)
        setEditing(t)
    }

    const toggleRole = (role: DeviceRole, id: string, on: boolean) =>
        setDraft((d) => ({ ...d, roles: { ...d.roles, [role]: on ? [...(d.roles[role] ?? []), id] : (d.roles[role] ?? []).filter((x) => x !== id) } }))

    const remove = (id: string) =>
        setDraft((d) => ({
            ...d,
            devices: d.devices.filter((x) => x.id !== id),
            roles: Object.fromEntries(Object.entries(d.roles).map(([r, ids]) => [r, (ids ?? []).filter((x) => x !== id)])),
        }))

    const rename = (id: string, name: string) => setDraft((d) => ({ ...d, devices: d.devices.map((x) => (x.id === id ? { ...x, name } : x)) }))

    const save = async () => {
        if (!editing) return
        setSaving(true)
        const res = await TerminalModel.saveDeviceSetup(editing._id, draft)
        setSaving(false)
        if (!res.success) return toast.error(errorOf(res.error, 'Could not save the devices'))
        toast.success('Devices saved', { description: 'The device uses them after its next settings refresh.' })
        setEditing(null)
        load()
    }

    const rolesOf = (setup: DeviceSetup | null) => DEVICE_ROLES.filter((r) => (setup?.roles[r] ?? []).length > 0)

    return (
        <Card>
            <CardHeader>
                <CardTitle>POS hardware</CardTitle>
                <CardDescription>Printers, scales and drawers each terminal uses. Hardware is added on the device; manage it here.</CardDescription>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <Spinner />
                    </div>
                ) : terminals.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">No terminals yet.</p>
                ) : (
                    <ul className="divide-y divide-border-subtle">
                        {terminals.map((t) => (
                            <li key={t._id} className="flex items-center justify-between gap-4 py-3">
                                <div className="min-w-0">
                                    <p className="truncate text-sm text-ink">
                                        {t.name}
                                        {t.branch?.name ? ` · ${t.branch.name}` : ''}
                                    </p>
                                    <p className="truncate text-xs text-muted-foreground">
                                        {t.device_setup?.devices.length
                                            ? `${t.device_setup.devices.map((d) => d.name).join(', ')} · ${rolesOf(t.device_setup).map((r) => ROLE_LABEL[r]).join(', ') || 'no roles'}`
                                            : 'No hardware backed up from this terminal'}
                                    </p>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => open(t)}>
                                    {editable ? 'Manage' : 'View'}
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent size="lg">
                    <DialogHeader>
                        <DialogTitle>{editing?.name} devices</DialogTitle>
                        <DialogDescription>
                            {draft.updated_at ? `Last saved ${new Date(draft.updated_at).toLocaleString()}.` : 'Nothing saved from this terminal yet.'} New
                            hardware is added on the device.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="max-h-[60vh] space-y-5 overflow-y-auto py-2">
                        {draft.devices.length === 0 && <p className="text-sm text-muted-foreground">No devices.</p>}
                        {draft.devices.map((d) => (
                            <div key={d.id} className="flex flex-wrap items-center gap-3">
                                <Input className="max-w-xs" value={d.name} disabled={!editable} onChange={(e) => rename(d.id, e.target.value)} aria-label="Device name" />
                                <span className="text-xs text-muted-foreground">
                                    {modelOf(d.model_id)?.name ?? 'Unknown model'} · {TRANSPORT_LABEL[d.transport] ?? d.transport}
                                </span>
                                {!modelOf(d.model_id) && <Badge tone="warning">Not in catalog</Badge>}
                                {editable && (
                                    <Button variant="ghost" size="sm" className="ml-auto" onClick={() => remove(d.id)}>
                                        Remove
                                    </Button>
                                )}
                            </div>
                        ))}

                        {draft.devices.length > 0 && (
                            <div className="space-y-2">
                                <Label>Which device does what</Label>
                                <div className="divide-y divide-border-subtle rounded-md border border-border">
                                    {DEVICE_ROLES.filter((role) => draft.devices.some((d) => modelOf(d.model_id)?.roles.includes(role))).map((role) => (
                                        <div key={role} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
                                            <span className="w-52 text-ink">{ROLE_LABEL[role]}</span>
                                            {draft.devices
                                                .filter((d) => modelOf(d.model_id)?.roles.includes(role))
                                                .map((d) => (
                                                    <label key={d.id} className="flex items-center gap-1.5">
                                                        <Checkbox
                                                            disabled={!editable}
                                                            checked={draft.roles[role]?.includes(d.id) ?? false}
                                                            onCheckedChange={(v) => toggleRole(role, d.id, v === true)}
                                                        />
                                                        {d.name}
                                                    </label>
                                                ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid gap-3 sm:grid-cols-2">
                            <label className="flex items-center justify-between gap-3 text-sm">
                                Print after every sale
                                <Switch
                                    disabled={!editable}
                                    checked={!!draft.options?.auto_print}
                                    onCheckedChange={(v) => setDraft((d) => ({ ...d, options: { ...d.options, auto_print: v } }))}
                                />
                            </label>
                            <label className="flex items-center justify-between gap-3 text-sm">
                                Open the cash drawer on cash sales
                                <Switch
                                    disabled={!editable}
                                    checked={!!draft.options?.open_drawer_on_cash}
                                    onCheckedChange={(v) => setDraft((d) => ({ ...d, options: { ...d.options, open_drawer_on_cash: v } }))}
                                />
                            </label>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
                            {editable ? 'Cancel' : 'Close'}
                        </Button>
                        {editable && (
                            <Button onClick={save} disabled={saving}>
                                {saving && <Spinner />}
                                Save
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    )
}
